import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DailyGridHeader from "./DailyGridHeader";
import StickyHeaderBackdrop from "./StickyHeaderBackdrop";
import DailyProductRow from "./DailyProductRow";
import { extractUniqueBadges } from "../utils/productNameBadges";
import { parsePastedRow } from "../utils/parsePaste";
import { useGridSelection, rectContains, type SelectionRect } from "../utils/useGridSelection";
import { type CellStatus } from "../utils/useSaveStatus";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";
import { useElementHeight } from "../utils/useElementHeight";

const SUM_COL_WIDTH = 120;
const DAY_COL_WIDTH = 60;
const MIN_DAY_COUNT = 5;
const WIDTH_SAFETY_PX = 2;

export function computeDayCount(containerWidth: number): number {
  const available = containerWidth - PLAN_PRODUCT_COL_WIDTH - SUM_COL_WIDTH - WIDTH_SAFETY_PX;
  return Math.max(MIN_DAY_COUNT, Math.floor(available / DAY_COL_WIDTH));
}

interface DayCell {
  period_date: string;
  quantity: number;
  manual: boolean;
}

export interface DailyRow {
  bottom_up_product_id: string;
  product_external_id: string;
  product_name: string;
  product_short_name: string | null;
  product_sku: string | null;
  product_line_id: string;
  product_line_name: string | null;
  product_line_ancestors?: string[];
  asp: { price: number; currency: string } | null;
  cogs: number | null;
  prices_by_level: Record<string, number>;
  month_target: number;
  days: DayCell[];
}

export interface PriceLevelInfo {
  id: string;
  name: string;
  color: string | null;
}

interface Props {
  days: string[];
  rows: DailyRow[];
  planCurrency?: string | null;
  editable: boolean;
  // First editable ISO date; earlier days render read-only (elapsed days).
  lockedBefore?: string;
  // Height of whatever sticks above the grid in the layout's scroll pane
  // (the StickyBar toolbar); header rows offset their sticky top by it.
  stickyOffset?: number;
  // quantity null = the user cleared the cell: saved as 0 without the manual
  // flag so Distribute can fill it again. A typed 0 stays manual.
  onCellChange: (productExternalId: string, periodDate: string, quantity: number | null) => void;
  onPasteCells: (
    productExternalId: string,
    updates: { period_date: string; quantity: number | null }[],
  ) => void;
  priceLevels?: PriceLevelInfo[];
  pricingByDate?: Record<string, string>;
  pricingLabelsByDate?: Record<string, string>;
  planNodeOrder?: string[];
  saveStatusFor?: (productExternalId: string, periodDate: string) => CellStatus | undefined;
  onDayCountChange?: (count: number) => void;
  selectedDates?: Set<string>;
  onToggleDate?: (date: string, shiftKey: boolean) => void;
  onDragSelect?: (dates: string[]) => void;
  onDistributeRow?: (row: DailyRow) => void;
}

export default function BottomUpDailyGrid({
  days,
  rows,
  planCurrency,
  editable,
  lockedBefore = "",
  stickyOffset = 0,
  onCellChange,
  onPasteCells,
  priceLevels = [],
  pricingByDate = {},
  pricingLabelsByDate = {},
  planNodeOrder,
  saveStatusFor,
  onDayCountChange,
  selectedDates,
  onToggleDate,
  onDragSelect,
  onDistributeRow,
}: Props) {
  const isLocked = useCallback((iso: string) => iso < lockedBefore, [lockedBefore]);

  // Paste a row of numbers starting at the anchored day, filling rightward
  // across the visible day columns. Overflow is silently ignored.
  const handlePasteRow = (productExternalId: string, anchorDate: string, text: string) => {
    const values = parsePastedRow(text);
    if (values.length === 0) return;
    const start = days.indexOf(anchorDate);
    if (start === -1) return;
    const updates: { period_date: string; quantity: number }[] = [];
    for (let i = 0; i < values.length && start + i < days.length; i++) {
      if (!isLocked(days[start + i])) updates.push({ period_date: days[start + i], quantity: values[i] });
    }
    if (updates.length > 0) onPasteCells(productExternalId, updates);
  };
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Header rows stick to the layout's scroll pane; the page body scrolls as
  // one, so the grid no longer owns a bounded scroll container.
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const theadHeight = useElementHeight(theadRef);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !onDayCountChange) return;
    const apply = (width: number) => onDayCountChange(computeDayCount(width));
    apply(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onDayCountChange]);

  const priceLevelById = useMemo(() => {
    const m = new Map<string, PriceLevelInfo>();
    priceLevels.forEach((pl) => m.set(pl.id, pl));
    return m;
  }, [priceLevels]);

  const grouped = useMemo(() => {
    const groups = new Map<string, DailyRow[]>();
    for (const r of rows) {
      const arr = groups.get(r.product_line_id) ?? [];
      arr.push(r);
      groups.set(r.product_line_id, arr);
    }
    const orderIndex = new Map<string, number>();
    (planNodeOrder ?? []).forEach((id, i) => orderIndex.set(id, i));
    const entries = Array.from(groups.entries()).map(([id, list]) => ({
      id,
      name: list[0]?.product_line_name ?? "(unknown)",
      ancestor_names: list[0]?.product_line_ancestors ?? [],
      rows: list,
      badges: extractUniqueBadges(list.map((r) => r.product_name)),
    }));
    entries.sort((a, b) => {
      const ai = orderIndex.has(a.id) ? (orderIndex.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.id) ? (orderIndex.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
    return entries;
  }, [rows, planNodeOrder]);

  // Flat row order (across groups) for rectangular selection indexing.
  const flatRows = useMemo(() => grouped.flatMap((g) => g.rows), [grouped]);
  const rowIndexById = useMemo(() => {
    const m = new Map<string, number>();
    flatRows.forEach((r, i) => m.set(r.bottom_up_product_id, i));
    return m;
  }, [flatRows]);

  const handleFillRange = useCallback(
    (rect: SelectionRect, quantity: number | null) => {
      for (let r = rect.r0; r <= rect.r1 && r < flatRows.length; r++) {
        const row = flatRows[r];
        const updates: { period_date: string; quantity: number | null }[] = [];
        for (let c = rect.c0; c <= rect.c1 && c < days.length; c++) {
          if (!isLocked(days[c])) updates.push({ period_date: days[c], quantity });
        }
        if (updates.length > 0) onPasteCells(row.product_external_id, updates);
      }
    },
    [flatRows, days, onPasteCells, isLocked],
  );

  // Delete/Backspace on a range clears the cells (drops their manual flags).
  const handleDeleteRange = useCallback(
    (rect: SelectionRect) => handleFillRange(rect, null),
    [handleFillRange],
  );

  const { isSelected, onCellMouseDown, clear: clearSelection, multi, rect } =
    useGridSelection(handleDeleteRange);

  // Live fill preview: the anchor cell reports its value as the user types and
  // every other selected cell displays it. Display-only until commit.
  const [previewQty, setPreviewQty] = useState<number | null>(null);
  useEffect(() => {
    if (!multi) setPreviewQty(null);
  }, [multi]);

  const rowIndexByExternalId = useMemo(() => {
    const m = new Map<string, number>();
    flatRows.forEach((r, i) => m.set(r.product_external_id, i));
    return m;
  }, [flatRows]);

  // Commit from a cell input. When the cell sits inside an active multi-cell
  // selection and the user actually typed the value, fan it out to the whole
  // rectangle (Excel-style fill); otherwise it's a normal single-cell edit.
  const handleCellCommit = useCallback(
    (productExternalId: string, periodDate: string, quantity: number | null, fillSelection?: boolean) => {
      const r = rowIndexByExternalId.get(productExternalId);
      const c = days.indexOf(periodDate);
      if (fillSelection && multi && rect && r !== undefined && c !== -1 && rectContains(rect, r, c)) {
        handleFillRange(rect, quantity);
        clearSelection();
      } else {
        onCellChange(productExternalId, periodDate, quantity);
      }
    },
    [rowIndexByExternalId, days, multi, rect, handleFillRange, clearSelection, onCellChange],
  );

  // Clicking a new cell resets the selection before the edited cell's blur can
  // fire — so flush a pending typed value into the rectangle here, while the
  // rect still exists. The input marks itself committed so its blur is a no-op.
  const handleCellMouseDown = useCallback(
    (r: number, c: number, shiftKey: boolean) => {
      if (!shiftKey && multi && rect) {
        const el = document.activeElement;
        if (
          el instanceof HTMLInputElement &&
          el.dataset.dirty === "true" &&
          el.dataset.committed !== "true"
        ) {
          const er = parseInt(el.dataset.row ?? "", 10);
          const ec = parseInt(el.dataset.col ?? "", 10);
          // Skip when clicking inside the cell being edited (caret placement).
          if (!Number.isNaN(er) && !Number.isNaN(ec) && rectContains(rect, er, ec) && !(er === r && ec === c)) {
            const digits = el.value.replace(/[^\d]/g, "");
            handleFillRange(rect, digits ? parseInt(digits, 10) : null);
            el.dataset.committed = "true";
          }
        }
      }
      onCellMouseDown(r, c, shiftKey);
    },
    [multi, rect, handleFillRange, onCellMouseDown],
  );

  const colSpan = 2 + days.length;

  return (
    <div ref={containerRef}>
      <StickyHeaderBackdrop stickyOffset={stickyOffset} theadHeight={theadHeight} />
      <table
        className="border-collapse text-xs"
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <DailyGridHeader
          ref={theadRef}
          top={stickyOffset}
          days={days}
          pricingByDate={pricingByDate}
          pricingLabelsByDate={pricingLabelsByDate}
          priceLevelById={priceLevelById}
          selectedDates={selectedDates}
          onToggleDate={onToggleDate}
          onDragSelect={onDragSelect}
          isLocked={isLocked}
        />

        <tbody>
          {grouped.map((group) => (
            <Fragment key={group.id}>
              <tr style={{ borderColor: "var(--accent)", background: "var(--muted)" }} className="border-b">
                <td
                  style={{ background: "var(--muted)", borderColor: "var(--border)", width: PLAN_PRODUCT_COL_WIDTH }}
                  className="sticky left-0 z-10 px-3 py-2 border-r"
                >
                  <div
                    style={{ color: "var(--muted-foreground)" }}
                    className="text-[10px] uppercase tracking-wider truncate"
                    title={[...group.ancestor_names, group.name].join(" › ")}
                  >
                    {group.ancestor_names.map((a) => (
                      <span key={a}>{a} › </span>
                    ))}
                    <span style={{ color: "var(--foreground)" }} className="font-semibold">
                      {group.name}
                    </span>
                  </div>
                </td>
                {days.map((d) => (
                  <td key={d} className="px-1.5 py-2" />
                ))}
                <td />
              </tr>

              {group.rows.map((r) => (
                <DailyProductRow
                  key={r.bottom_up_product_id}
                  row={r}
                  planCurrency={planCurrency}
                  displayName={r.product_short_name || group.badges.get(r.product_name) || r.product_name}
                  days={days}
                  editable={editable}
                  isLocked={isLocked}
                  rowIndex={rowIndexById.get(r.bottom_up_product_id) ?? 0}
                  isSelected={isSelected}
                  onCellMouseDown={handleCellMouseDown}
                  onCellCommit={handleCellCommit}
                  onPasteRow={handlePasteRow}
                  saveStatusFor={saveStatusFor}
                  pricingByDate={pricingByDate}
                  pricingLabelsByDate={pricingLabelsByDate}
                  priceLevelById={priceLevelById}
                  preview={previewQty}
                  onPreview={setPreviewQty}
                  onDistributeRow={onDistributeRow}
                />
              ))}
            </Fragment>
          ))}

          {rows.length === 0 && (
            <tr>
              <td
                colSpan={colSpan}
                style={{ color: "var(--chart-muted)" }}
                className="px-4 py-8 text-center text-sm"
              >
                No products found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
